import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, Calendar, Clock, Repeat } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ReviewDialog } from '@/components/ReviewDialog';
import { format } from 'date-fns';
import { convertFromUTC, getUserTimezone } from '@/utils/timezoneUtils';

interface PendingReviewBooking {
  id: string;
  booked_date: string;
  start_time: string;
  end_time: string;
  notes?: string;
  mentee_id: string;
  application_uuid: string; // UUID from session_bookings.application_id
  application_id: string; // Text ID from applications.application_id
  mentee: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

interface PendingSubscriptionReview {
  id: string;
  application_id: string; // Text ID from applications.application_id
  subscription_end_date: string;
  mentee_id: string;
  course_title: string;
  mentee: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

interface MentorLeaveReviewProps {
  onReviewSubmitted?: () => void;
  onPendingReviewsChange?: (hasPendingReviews: boolean) => void;
}

const MentorLeaveReview = ({ onReviewSubmitted, onPendingReviewsChange }: MentorLeaveReviewProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const userTimezone = getUserTimezone();
  const [pendingReviews, setPendingReviews] = useState<PendingReviewBooking[]>([]);
  const [pendingSubscriptionReviews, setPendingSubscriptionReviews] = useState<PendingSubscriptionReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState<{
    isOpen: boolean;
    booking?: PendingReviewBooking;
    subscription?: PendingSubscriptionReview;
    isSubscriptionReview?: boolean;
  }>({ isOpen: false });

  useEffect(() => {
    if (user) {
      fetchPendingReviews();
    }
  }, [user]);

  useEffect(() => {
    // Listen for subscription-ended events to refresh the pending reviews
    const handleSubscriptionEnded = () => {
      console.log('DEBUG: Subscription ended event received, refreshing reviews');
      fetchPendingReviews();
    };

    // Listen for session-marked-complete events to refresh the pending reviews
    const handleSessionMarkedComplete = () => {
      console.log('DEBUG: Session marked complete event received, refreshing reviews');
      fetchPendingReviews();
    };

    window.addEventListener('subscription-ended', handleSubscriptionEnded);
    window.addEventListener('session-marked-complete', handleSessionMarkedComplete);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('subscription-ended', handleSubscriptionEnded);
      window.removeEventListener('session-marked-complete', handleSessionMarkedComplete);
    };
  }, []);

  const fetchPendingReviews = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      console.log('DEBUG: Fetching pending reviews for mentor:', user.id);

      // Fetch session bookings for completed applications that don't have mentor reviews yet
      const { data: bookings, error: bookingsError } = await supabase
        .from('session_bookings')
        .select(`
          id,
          booked_date,
          start_time,
          end_time,
          notes,
          mentee_id,
          application_id,
          applications!inner(
            application_id
          )
        `)
        .eq('mentor_id', user.id)
        .eq('status', 'completed')
        .eq('mentor_review_submitted', false)
        .order('booked_date', { ascending: false });

      if (bookingsError) {
        console.error('DEBUG: Error fetching session bookings:', bookingsError);
        throw bookingsError;
      }

      console.log('DEBUG: Session bookings found:', bookings?.length || 0);
      
      // Get unique mentee IDs and fetch their profiles
      const uniqueMenteeIds = [...new Set(bookings?.map(b => b.mentee_id) || [])];
      let menteeProfiles = [];
      
      if (uniqueMenteeIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .in('id', uniqueMenteeIds);

        if (profilesError) {
          console.error('DEBUG: Error fetching mentee profiles:', profilesError);
        } else {
          menteeProfiles = profiles || [];
        }
      }

      // Filter bookings that don't have reviews yet
      const bookingsWithoutReviews = [];
      for (const booking of bookings || []) {
        if (!booking.application_id || !booking.applications?.application_id) continue;

        // Check if mentor review already exists for this session booking
        const { data: existingReview, error: reviewError } = await supabase
          .from('appointment_reviews')
          .select('id')
          .eq('booking_id', booking.id)
          .eq('reviewer_id', user.id)
          .maybeSingle();

        if (reviewError) {
          console.error('DEBUG: Error checking existing review:', reviewError);
          continue;
        }

        if (!existingReview) {
          // Add mentee profile to booking
          const menteeProfile = menteeProfiles.find(p => p.id === booking.mentee_id);
          bookingsWithoutReviews.push({
            ...booking,
            application_uuid: booking.application_id,
            application_id: booking.applications.application_id,
            mentee: menteeProfile ? {
              first_name: menteeProfile.first_name,
              last_name: menteeProfile.last_name,
              avatar_url: menteeProfile.avatar_url
            } : {
              first_name: '',
              last_name: '',
              avatar_url: ''
            }
          });
        }
      }

      setPendingReviews(bookingsWithoutReviews);

      // Fetch ended subscriptions for mentor reviews - Now they should have 'completed' status
      console.log('DEBUG: Fetching ended subscriptions for mentor:', user.id);
      
      let subscriptionsWithoutReviews: PendingSubscriptionReview[] = [];
      
      try {
        // Ensure ended subscriptions are marked as completed before querying
        await supabase.rpc('update_ended_subscriptions');

        // Get all completed subscription applications for this mentor
        const { data: applications, error: applicationsError } = await supabase
          .from('applications')
          .select(`
            id,
            application_id,
            subscription_end_date,
            user_id,
            course_id,
            status,
            is_subscription_active
          `)
          .eq('mentor_id', user.id)
          .eq('status', 'completed')
          .eq('is_subscription_active', false)
          .not('subscription_end_date', 'is', null)
          .lt('subscription_end_date', new Date().toISOString())
          .order('subscription_end_date', { ascending: false });

        if (applicationsError) {
          console.error('DEBUG: Error fetching applications:', applicationsError);
          throw applicationsError;
        }

        console.log('DEBUG: Found completed subscription applications:', applications?.length || 0);
        
        if (!applications || applications.length === 0) {
          console.log('DEBUG: No completed subscription applications found');
          setPendingSubscriptionReviews([]);
        } else {
          // Get course data for each application and check for existing reviews
          subscriptionsWithoutReviews = [];
          
          for (const application of applications) {
            console.log('DEBUG: Processing application:', application.id, 'for mentee:', application.user_id);

            // Check if mentor review already exists using text application_id
            const { data: existingReview, error: reviewError } = await supabase
              .from('subscription_reviews')
              .select('id')
              .eq('application_id', application.application_id)
              .eq('reviewer_id', user.id)
              .eq('review_type', 'mentor_to_mentee')
              .maybeSingle();

            if (reviewError) {
              console.error('DEBUG: Error checking existing review:', reviewError);
              continue;
            }

            if (existingReview) {
              console.log('DEBUG: Review already exists for application:', application.id);
              continue;
            }

            // Get course data
            const { data: courseData, error: courseError } = await supabase
              .from('courses')
              .select('title, course_type')
              .eq('id', application.course_id)
              .single();

            if (courseError) {
              console.error('DEBUG: Error fetching course data:', courseError);
              continue;
            }

            // Only include subscription courses
            if (courseData?.course_type !== 'subscription') {
              console.log('DEBUG: Skipping non-subscription course:', courseData?.course_type);
              continue;
            }

            // Get mentee profile data
            const { data: menteeProfile, error: profileError } = await supabase
              .from('profiles')
              .select('first_name, last_name, avatar_url')
              .eq('id', application.user_id)
              .single();

            if (profileError) {
              console.error('DEBUG: Error fetching mentee profile:', profileError);
              continue;
            }

            console.log('DEBUG: Adding subscription without review:', application.id);
            subscriptionsWithoutReviews.push({
              id: application.id,
              application_id: application.application_id,
              subscription_end_date: application.subscription_end_date!,
              mentee_id: application.user_id,
              course_title: courseData?.title || 'Subscription',
              mentee: {
                first_name: menteeProfile?.first_name || '',
                last_name: menteeProfile?.last_name || '',
                avatar_url: menteeProfile?.avatar_url
              }
            });
          }

          console.log('DEBUG: Final subscriptions without reviews:', subscriptionsWithoutReviews.length);
          setPendingSubscriptionReviews(subscriptionsWithoutReviews);
        }

      } catch (subscriptionError) {
        console.error('DEBUG: Error in subscription fetch logic:', subscriptionError);
        setPendingSubscriptionReviews([]);
      }

      // Notify parent component about pending reviews status
      const totalPendingReviews = bookingsWithoutReviews.length + subscriptionsWithoutReviews.length;
      onPendingReviewsChange?.(totalPendingReviews > 0);

    } catch (error) {
      console.error('DEBUG: Full error object:', error);
      console.error('Error fetching pending reviews:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Error",
        description: `Failed to load sessions pending review: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveReview = (booking: PendingReviewBooking) => {
    console.log('DEBUG: Leaving review for session booking:', booking.id, 'application:', booking.application_id);
    setReviewDialog({ isOpen: true, booking, isSubscriptionReview: false });
  };

  const handleLeaveSubscriptionReview = (subscription: PendingSubscriptionReview) => {
    console.log('DEBUG: Leaving review for subscription application:', subscription.id);
    setReviewDialog({ isOpen: true, subscription, isSubscriptionReview: true });
  };

  const handleReviewSubmitted = () => {
    console.log('DEBUG: Review submitted, refreshing data and notifying parent');
    
    // Immediately remove from local state (like mentees do)
    if (reviewDialog.isSubscriptionReview && reviewDialog.subscription) {
      setPendingSubscriptionReviews(prev => 
        prev.filter(s => s.id !== reviewDialog.subscription!.id)
      );
    } else if (reviewDialog.booking) {
      setPendingReviews(prev => 
        prev.filter(b => b.id !== reviewDialog.booking!.id)
      );
    }
    
    setReviewDialog({ isOpen: false });
    fetchPendingReviews(); // Keep for data consistency
    onReviewSubmitted?.();
    
    console.log('DEBUG: Dispatching review-submitted event');
    window.dispatchEvent(new CustomEvent('review-submitted', {
      detail: { mentorId: user?.id }
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalPendingReviews = pendingReviews.length + pendingSubscriptionReviews.length;

  return (
    <>
      <div className="space-y-4">
        {/* Session Reviews */}
        {pendingReviews.map((booking) => (
          <Card key={`session-${booking.id}`} className="p-3 sm:p-4 border-l-4 border-l-orange-500 bg-orange-50/50">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                    <AvatarImage src={booking.mentee.avatar_url} />
                    <AvatarFallback className="bg-emu-blue text-white">
                      {booking.mentee.first_name?.[0]}{booking.mentee.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                      {booking.mentee.first_name} {booking.mentee.last_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        {format(convertFromUTC(booking.booked_date, booking.start_time, userTimezone).localDate, 'EEE, MMM d')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                        {convertFromUTC(booking.booked_date, booking.start_time, userTimezone).localTime} - {convertFromUTC(booking.booked_date, booking.end_time, userTimezone).localTime}
                      </span>
                      <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">
                        Awaiting Review
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="w-full sm:w-auto">
                  <Button
                    onClick={() => handleLeaveReview(booking)}
                    className="bg-emu-blue hover:bg-emu-blue/90 text-white w-full sm:w-auto"
                    size="sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Leave Review
                  </Button>
                </div>
              </div>

              {booking.notes && (
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mt-3 sm:mt-4">
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{booking.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Subscription Reviews */}
        {pendingSubscriptionReviews.map((subscription) => (
          <Card key={`subscription-${subscription.id}`} className="p-3 sm:p-4 border-l-4 border-l-purple-500 bg-purple-50/50">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3 sm:gap-4">
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                    <AvatarImage src={subscription.mentee.avatar_url} />
                    <AvatarFallback className="bg-emu-blue text-white">
                      {subscription.mentee.first_name?.[0]}{subscription.mentee.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                      {subscription.mentee.first_name} {subscription.mentee.last_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Repeat className="h-3 w-3 sm:h-4 sm:w-4" />
                        {subscription.course_title}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        Ended {format(
                          new Date(
                            parseInt(subscription.subscription_end_date.substring(0, 4)),
                            parseInt(subscription.subscription_end_date.substring(5, 7)) - 1,
                            parseInt(subscription.subscription_end_date.substring(8, 10))
                          ),
                          'MMM d, yyyy'
                        )}
                      </span>
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                        Awaiting Review
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="w-full sm:w-auto">
                  <Button
                    onClick={() => handleLeaveSubscriptionReview(subscription)}
                    className="bg-emu-blue hover:bg-emu-blue/90 text-white w-full sm:w-auto"
                    size="sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Leave Review
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Review Dialog */}
      {(reviewDialog.booking || reviewDialog.subscription) && (
        <ReviewDialog
          isOpen={reviewDialog.isOpen}
          onClose={() => setReviewDialog({ isOpen: false })}
          bookingId={reviewDialog.isSubscriptionReview 
            ? reviewDialog.subscription!.application_id 
            : reviewDialog.booking!.id
          }
          revieweeId={reviewDialog.isSubscriptionReview ? reviewDialog.subscription!.mentee_id : reviewDialog.booking!.mentee_id}
          revieweeName={reviewDialog.isSubscriptionReview 
            ? `${reviewDialog.subscription!.mentee.first_name} ${reviewDialog.subscription!.mentee.last_name}`
            : `${reviewDialog.booking!.mentee.first_name} ${reviewDialog.booking!.mentee.last_name}`
          }
          onReviewSubmitted={handleReviewSubmitted}
          isSubscriptionReview={reviewDialog.isSubscriptionReview}
          reviewType="mentor_to_mentee"
        />
      )}
    </>
  );
};

export default MentorLeaveReview;
